import React, { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Inbox as InboxIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { horseApi, Horse } from '../../utils/api';
import { withOfflineFallback, readHorsesPage, skipWhenOffline } from '../../offline';
import { inboxService } from '../../services/inboxService';
import { UnallocatedItem } from '../../types/inbox';
import UnallocatedItemCard from '../../components/Inbox/UnallocatedItemCard';
import AssignToPatientDialog from '../../components/Inbox/AssignToPatientDialog';
import { useRBAC } from '../../auth/useRBAC';
import { resultsInboxControls } from '../../auth/pageControls';

type FilterKey = 'PENDING' | 'TRIAGED' | 'ALL';

const FILTER_LABEL: Record<FilterKey, string> = {
  PENDING: 'Pending',
  TRIAGED: 'Triaged',
  ALL: 'All',
};

const matchesFilter = (item: UnallocatedItem, f: FilterKey): boolean => {
  if (f === 'ALL') return true;
  if (f === 'PENDING') return item.triageStatus === 'PENDING';
  return item.triageStatus !== 'PENDING';
};

const InboxPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  // unallocated-results.controller.ts assign/reject/reopen all declare manage:medical_records
  // (INV-108). The card renders each action only when its handler is defined, so a person who may
  // READ the inbox but not triage it gets the list and no buttons — no second code path.
  const rbac = useRBAC();
  const can = resultsInboxControls(rbac);

  const [filter, setFilter] = useState<FilterKey>('PENDING');
  const [assignTarget, setAssignTarget] = useState<UnallocatedItem | null>(null);

  const online = skipWhenOffline();
  const { data: items = [], isLoading, isError } = useQuery<UnallocatedItem[]>(
    ['inbox', 'ALL'],
    () => inboxService.list('ALL'),
    { enabled: online },
  );

  // The auth interceptor unconditionally attaches the token (dev-bypass or
  // real JWT) regardless of useAuth() state, so this query is safe to fire
  // on mount without an `enabled: !!user` gate. With the gate in place,
  // initial mount could leave horses=[] stuck if the user-flip-to-truthy
  // happened during the first render (the query observer wasn't subscribed
  // yet to receive the enabled flip).
  const { data: horses = [] } = useQuery<Horse[]>(
    ['horses-list-all'],
    withOfflineFallback(
      () => horseApi.list(),
      async () => (await readHorsesPage({ page: 1, limit: 100000 })).data as Horse[],
    ),
    { staleTime: 60_000, refetchOnMount: true },
  );

  const counts = useMemo(
    () => ({
      PENDING: items.filter(i => i.triageStatus === 'PENDING').length,
      TRIAGED: items.filter(i => i.triageStatus !== 'PENDING').length,
      ALL: items.length,
    }),
    [items],
  );

  const filtered = useMemo(() => items.filter(i => matchesFilter(i, filter)), [items, filter]);

  // Pre-score all visible items so the suggestion list comes for free.
  const suggestionsByItem = useMemo(() => {
    const m = new Map<string, ReturnType<typeof inboxService.scoreMatches>>();
    for (const item of filtered) m.set(item.id, inboxService.scoreMatches(item, horses));
    return m;
  }, [filtered, horses]);

  const assignMutation = useMutation(
    (args: { id: string; horseId: string }) => inboxService.assign(args.id, args.horseId),
    {
      onSuccess: (updated) => {
        const horse = horses.find(h => h.id === updated.assignedHorseId);
        enqueueSnackbar(`Assigned to ${horse?.name ?? 'patient'}`, { variant: 'success' });
        queryClient.invalidateQueries(['inbox']);
        // The server moved the result onto the horse; refresh its chart lists.
        if (updated.assignedHorseId) {
          queryClient.invalidateQueries(['lab-results', updated.assignedHorseId]);
          queryClient.invalidateQueries(['horse-keystone-studies', updated.assignedHorseId]);
        }
        setAssignTarget(null);
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message ?? 'Assign failed', { variant: 'error' });
      },
    },
  );

  const rejectMutation = useMutation(
    (args: { id: string; status: 'REJECTED' | 'NOT_OURS'; reason?: string }) =>
      inboxService.reject(args.id, args.status, args.reason),
    {
      onSuccess: () => {
        enqueueSnackbar('Removed from inbox', { variant: 'info' });
        queryClient.invalidateQueries(['inbox']);
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message ?? 'Reject failed', { variant: 'error' });
      },
    },
  );

  const reopenMutation = useMutation(
    (id: string) => inboxService.reopen(id),
    {
      onSuccess: () => {
        enqueueSnackbar('Re-opened', { variant: 'info' });
        queryClient.invalidateQueries(['inbox']);
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message ?? 'Re-open failed', { variant: 'error' });
      },
    },
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <InboxIcon color="primary" />
          <Typography variant="h5">Unallocated Results</Typography>
          {counts.PENDING > 0 && (
            <Chip label={`${counts.PENDING} pending`} color="warning" size="small" />
          )}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Lab and imaging results that arrived without a clean link to a patient land here. Review,
          assign to the right horse, or reject duplicates / wrong-clinic results.
        </Typography>
      </Alert>

      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {(['PENDING', 'TRIAGED', 'ALL'] as FilterKey[]).map(f => (
          <Chip
            key={f}
            label={`${FILTER_LABEL[f]}${counts[f] > 0 ? ` (${counts[f]})` : ''}`}
            onClick={() => setFilter(f)}
            color={filter === f ? 'primary' : 'default'}
            variant={filter === f ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Box>

      {/* A failed or skipped load must never read as "nothing waiting". */}
      {!online || isError ? (
        <Alert severity="warning">
          {!online
            ? 'You are offline. Unallocated results load when you are back online.'
            : 'Unallocated results could not be loaded. Refresh the page to try again.'}
        </Alert>
      ) : isLoading ? (
        <Box py={6} textAlign="center">
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <InboxIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {filter === 'PENDING' ? 'No items awaiting triage. 🎉' : 'No items.'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map(item => {
            const suggestions = suggestionsByItem.get(item.id) ?? [];
            const busy =
              (assignMutation.isLoading && assignMutation.variables?.id === item.id) ||
              (rejectMutation.isLoading && rejectMutation.variables?.id === item.id) ||
              (reopenMutation.isLoading && reopenMutation.variables === item.id);
            return (
              <UnallocatedItemCard
                key={item.id}
                item={item}
                suggestions={suggestions}
                busy={busy}
                onAssignTopMatch={
                  can.assign && item.triageStatus === 'PENDING'
                    ? horseId => assignMutation.mutate({ id: item.id, horseId })
                    : undefined
                }
                onAssignManual={
                  can.assign && item.triageStatus === 'PENDING'
                    ? () => setAssignTarget(item)
                    : undefined
                }
                onReject={
                  can.reject && item.triageStatus === 'PENDING'
                    ? (status, reason) => rejectMutation.mutate({ id: item.id, status, reason })
                    : undefined
                }
                onReopen={
                  can.reopen && item.triageStatus !== 'PENDING'
                    ? () => reopenMutation.mutate(item.id)
                    : undefined
                }
              />
            );
          })}
        </Stack>
      )}

      <AssignToPatientDialog
        open={!!assignTarget}
        item={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssign={horseId => {
          if (assignTarget) assignMutation.mutate({ id: assignTarget.id, horseId });
        }}
        saving={assignMutation.isLoading}
      />
    </Container>
  );
};

export default InboxPage;
